/*
 * WorkAndBuhin.vue の setBuhinToWork() 周辺にある保存確定、status 更新、emit 判定を切り出した処理。
 */
import { BuhinDetail } from "@/model/Buhin";
import { toArray } from "@/components/work-buhin/WorkBuhinAdapter";
import { isJunseiBuhinCode } from "@/components/work-buhin/WorkBuhinMatch";
import { STATUS_SELECT_WORK_BUHIN } from "@/model/StatusSelectWorkBuhin";
import { INSPECT_TYPE } from "@/model/InspectType";

export interface WorkBuhinSaveOptions {
  activeBuhinShubetsuId: number;
  isRecommendManualWithOsusume: boolean;
}

export interface WorkBuhinSaveState {
  buhinList: BuhinDetail[];
  osusumeBuhinList?: BuhinDetail[];
  manualBuhinList: BuhinDetail[];
}

export interface WorkBuhinSaveApplyEffectState {
  buhinList: BuhinDetail[];
  shouldApplyOsusumeBuhinList: boolean;
  osusumeBuhinList: BuhinDetail[] | null;
  manualBuhinList: BuhinDetail[];
}

export type WorkBuhinSocketActionType = "none" | "add" | "remove";

export interface WorkBuhinSocketAction {
  type: WorkBuhinSocketActionType;
  payload: any;
  isSocket: boolean;
}

export interface WorkBuhinSocketApplyEffectState {
  shouldApplySocket: boolean;
  shouldSetSocket: boolean;
  isSocket: boolean;
  shouldRemoveSeibiId: boolean;
  removePayload: any;
  shouldAddSeibiId: boolean;
  addPayload: any;
}

export interface WorkBuhinStatusState {
  buhinSelected: number;
  shouldChangeWorkStatus: boolean;
  workBuhinSelected: number | null;
}

export interface WorkBuhinStatusApplyEffectState extends WorkBuhinStatusState {
  shouldSetWorkBuhinSelected: boolean;
  shouldRemoveEditMode: boolean;
}

export interface DeletedStatusForConfirmEmitState {
  shouldApply: boolean;
  dataBuhinSelected: number | null;
  seibiBuhinSelected: number | null;
  manualSeibiBuhinSelected: number | null;
}

export type DeletedStatusForConfirmEmitApplyEffectState =
  DeletedStatusForConfirmEmitState;

export interface DeletedStatusForConfirmEmitTargetApplyEffectState {
  shouldApply: boolean;
  shouldApplySeibiBuhinSelected: boolean;
  seibiBuhinSelectedIndex: number;
  seibiBuhinSelected: number | null;
  shouldApplyManualSeibiBuhinSelected: boolean;
  manualSeibiBuhinSelectedIndex: number;
  manualSeibiBuhinSelected: number | null;
  shouldApplyDataBuhinSelected: boolean;
  dataBuhinSelected: number | null;
}

export interface SeibiCleanupState {
  seibiList: any[] | null;
  manualSeibiList: any[] | null;
}

export interface SeibiCleanupApplyEffectState {
  shouldApplySeibiList: boolean;
  seibiList: any[] | null;
  shouldApplyManualSeibiList: boolean;
  manualSeibiList: any[] | null;
}

export interface SetBuhinToWorkAfterSaveActionState {
  changeList: any[];
  shouldApplyResetSeibiConfirm: boolean;
  shouldCommitBuhinConfirm: boolean;
  shouldEmitBuhinConfirm: boolean;
  doEmit: boolean;
}

export type SetBuhinToWorkAfterSaveApplyEffectState =
  SetBuhinToWorkAfterSaveActionState;

export interface BuhinConfirmPayloadParams {
  data: any;
  seibi: any;
  isManualWork: boolean;
  changeList: any[];
}

export interface BuhinConfirmEmitActionState {
  shouldApplyDeletedStatus: boolean;
  shouldEmit: boolean;
  eventName: string;
  payload: any;
}

export type BuhinConfirmEmitApplyEffectState = BuhinConfirmEmitActionState;

/*
 * getTargetWorkForBuhinSave を WorkAndBuhin.vue から分離し、
 * 保存確定 payload と status/emit 判定 を親側置換や単体テストで固定するため。
 */
export function getTargetWorkForBuhinSave(params: {
  data: any;
  activeWorkIndex: number;
  isManualWork: boolean;
}): any {
  if (params.data && params.data.seibiList) {
    if (params.isManualWork) {
      return params.data.manualSeibiList[params.activeWorkIndex];
    }
    return params.data.seibiList[params.activeWorkIndex];
  }
  return params.data;
}

/*
 * shouldSaveRecommendManualOsusume を WorkAndBuhin.vue から分離し、
 * 保存確定 payload と status/emit 判定 を親側置換や単体テストで固定するため。
 */
export function shouldSaveRecommendManualOsusume(work: any): boolean {
  return !!(work && work.sknInspectType == INSPECT_TYPE.RECOMMEND_MANUAL && work.osusumeId);
}

/*
 * resolveWorkBuhinSocketAction を WorkAndBuhin.vue から分離し、
 * 保存確定 payload と status/emit 判定 を親側置換や単体テストで固定するため。
 */
export function resolveWorkBuhinSocketAction(params: {
  data: any;
  work: any;
  status: number;
}): WorkBuhinSocketAction {
  const type = params.data && params.data.sknInspectType;
  if (!(type <= 1)) {
    return {
      type: "none",
      payload: null,
      isSocket: false
    };
  }
  const payload = {
    ids: [params.work && params.work.seibiDetailId],
    type
  };
  if (
    (params.status == STATUS_SELECT_WORK_BUHIN.Initial ||
      params.status == STATUS_SELECT_WORK_BUHIN.delete) &&
    params.work &&
    params.work.buhinSelected
  ) {
    return {
      type: "remove",
      payload,
      isSocket: true
    };
  }
  return {
    type: "add",
    payload,
    isSocket: false
  };
}

/*
 * buildWorkBuhinSocketApplyEffectState を WorkAndBuhin.vue から分離し、
 * socket 通知で isSocket、removeSeibiId、addSeibiId のどれを実行するかを固定するため。
 */
export function buildWorkBuhinSocketApplyEffectState(
  socketAction: WorkBuhinSocketAction
): WorkBuhinSocketApplyEffectState {
  return {
    shouldApplySocket: socketAction.type !== "none",
    shouldSetSocket: socketAction.type === "remove",
    isSocket: socketAction.isSocket,
    shouldRemoveSeibiId: socketAction.type === "remove",
    removePayload: socketAction.type === "remove" ? socketAction.payload : null,
    shouldAddSeibiId: socketAction.type === "add",
    addPayload: socketAction.type === "add" ? socketAction.payload : null
  };
}

/*
 * resolveWorkBuhinStatusState を WorkAndBuhin.vue から分離し、
 * 保存確定 payload と status/emit 判定 を親側置換や単体テストで固定するため。
 */
export function resolveWorkBuhinStatusState(params: {
  status: number;
  editSeibi: boolean;
}): WorkBuhinStatusState {
  const isDelete =
    params.status == STATUS_SELECT_WORK_BUHIN.delete;
  const isReset =
    params.status == STATUS_SELECT_WORK_BUHIN.Initial ||
    params.status == STATUS_SELECT_WORK_BUHIN.delete;

  return {
    buhinSelected: isDelete ? STATUS_SELECT_WORK_BUHIN.Initial : params.status,
    shouldChangeWorkStatus: !!(params.editSeibi && isReset),
    workBuhinSelected: params.editSeibi && !isReset
      ? STATUS_SELECT_WORK_BUHIN.Done
      : null
  };
}

/*
 * buildWorkBuhinStatusApplyEffectState を WorkAndBuhin.vue から分離し、
 * 作業 status 反映、点検項目 status 更新、編集 mode 削除の実行条件を固定するため。
 */
export function buildWorkBuhinStatusApplyEffectState(
  statusState: WorkBuhinStatusState
): WorkBuhinStatusApplyEffectState {
  return {
    ...statusState,
    shouldSetWorkBuhinSelected: statusState.workBuhinSelected != null,
    shouldRemoveEditMode: true
  };
}

/*
 * buildBuhinChangeList を WorkAndBuhin.vue から分離し、
 * 保存確定 payload と status/emit 判定 を親側置換や単体テストで固定するため。
 */
export function buildBuhinChangeList(buhinChangeList: any): any[] {
  if (!buhinChangeList || Object.keys(buhinChangeList).length === 0) {
    return [];
  }
  return Object.values(buhinChangeList).filter(
    (buhin: any) => buhin.oldBuhinPrice != buhin.newBuhinPrice
  );
}

/*
 * buildBuhinConfirmPayload を WorkAndBuhin.vue から分離し、
 * onconfirm emit の data.seibiList 有無による payload 形状を固定するため。
 */
export function buildBuhinConfirmPayload(
  params: BuhinConfirmPayloadParams
): any {
  if (params.data && params.data.seibiList) {
    return {
      inspectItem: params.data,
      seibi: params.seibi,
      notManual: !params.isManualWork,
      changeList: params.changeList
    };
  }
  return {
    ...params.data,
    changeList: params.changeList
  };
}

/*
 * buildBuhinConfirmEmitActionState を WorkAndBuhin.vue から分離し、
 * onconfirm emit 前の削除 status 反映条件、event 名、payload を固定するため。
 */
export function buildBuhinConfirmEmitActionState(params: {
  status: number;
  data: any;
  seibi: any;
  isManualWork: boolean;
  changeList: any[];
}): BuhinConfirmEmitActionState {
  return {
    shouldApplyDeletedStatus: params.status == STATUS_SELECT_WORK_BUHIN.delete,
    shouldEmit: true,
    eventName: "onconfirm",
    payload: buildBuhinConfirmPayload({
      data: params.data,
      seibi: params.seibi,
      isManualWork: params.isManualWork,
      changeList: params.changeList
    })
  };
}

/*
 * buildBuhinConfirmEmitApplyEffectState を WorkAndBuhin.vue から分離し、
 * onconfirm emit 時の削除 status 適用、event 名、payload の反映条件を固定するため。
 */
export function buildBuhinConfirmEmitApplyEffectState(
  actionState: BuhinConfirmEmitActionState
): BuhinConfirmEmitApplyEffectState {
  return {
    shouldApplyDeletedStatus: actionState.shouldApplyDeletedStatus,
    shouldEmit: actionState.shouldEmit,
    eventName: actionState.eventName,
    payload: actionState.payload
  };
}

/*
 * shouldEmitBuhinConfirm を WorkAndBuhin.vue から分離し、
 * 保存確定 payload と status/emit 判定 を親側置換や単体テストで固定するため。
 */
export function shouldEmitBuhinConfirm(params: {
  isBuhin: boolean;
  isDone: boolean;
  isWorkUnchanged: boolean;
  hasExplicitBuhinEdit?: boolean;
  suppressUnchangedNoBuhinConfirm?: boolean;
}): boolean {
  if (!params.isBuhin && !params.suppressUnchangedNoBuhinConfirm) {
    return true;
  }
  return !(params.isDone && params.isWorkUnchanged && !params.hasExplicitBuhinEdit);
}

/*
 * buildSetBuhinToWorkAfterSaveActionState を WorkAndBuhin.vue から分離し、
 * 保存後の reset confirm、commit、emit の実行条件と changeList を固定するため。
 */
export function buildSetBuhinToWorkAfterSaveActionState(params: {
  buhinChangeList: any;
  isBuhin: boolean;
  isDone: boolean;
  isWorkUnchanged: boolean;
  hasExplicitBuhinEdit?: boolean;
  suppressUnchangedNoBuhinConfirm?: boolean;
}): SetBuhinToWorkAfterSaveActionState {
  const changeList = buildBuhinChangeList(params.buhinChangeList);
  const doEmit = shouldEmitBuhinConfirm({
    isBuhin: params.isBuhin,
    isDone: params.isDone,
    isWorkUnchanged: params.isWorkUnchanged,
    hasExplicitBuhinEdit: params.hasExplicitBuhinEdit,
    suppressUnchangedNoBuhinConfirm: params.suppressUnchangedNoBuhinConfirm
  });
  return {
    changeList,
    shouldApplyResetSeibiConfirm: true,
    shouldCommitBuhinConfirm: doEmit,
    shouldEmitBuhinConfirm: doEmit,
    doEmit
  };
}

/*
 * buildSetBuhinToWorkAfterSaveApplyEffectState を WorkAndBuhin.vue から分離し、
 * setBuhinToWork 保存後の reset confirm、commit、emit、戻り値 doEmit の反映条件を固定するため。
 */
export function buildSetBuhinToWorkAfterSaveApplyEffectState(
  actionState: SetBuhinToWorkAfterSaveActionState
): SetBuhinToWorkAfterSaveApplyEffectState {
  return {
    changeList: actionState.changeList,
    shouldApplyResetSeibiConfirm: actionState.shouldApplyResetSeibiConfirm,
    shouldCommitBuhinConfirm: actionState.shouldCommitBuhinConfirm,
    shouldEmitBuhinConfirm: actionState.shouldEmitBuhinConfirm,
    doEmit: actionState.doEmit
  };
}

/*
 * resolveDeletedStatusForConfirmEmit を WorkAndBuhin.vue から分離し、
 * 保存確定 payload と status/emit 判定 を親側置換や単体テストで固定するため。
 */
export function resolveDeletedStatusForConfirmEmit(params: {
  status: number;
  hasSeibiList: boolean;
  isManualWork: boolean;
}): DeletedStatusForConfirmEmitState {
  if (params.status != STATUS_SELECT_WORK_BUHIN.delete) {
    return {
      shouldApply: false,
      dataBuhinSelected: null,
      seibiBuhinSelected: null,
      manualSeibiBuhinSelected: null
    };
  }
  if (params.hasSeibiList) {
    return {
      shouldApply: true,
      dataBuhinSelected: null,
      seibiBuhinSelected: STATUS_SELECT_WORK_BUHIN.delete,
      manualSeibiBuhinSelected: params.isManualWork
        ? STATUS_SELECT_WORK_BUHIN.delete
        : null
    };
  }
  return {
    shouldApply: true,
    dataBuhinSelected: STATUS_SELECT_WORK_BUHIN.delete,
    seibiBuhinSelected: null,
    manualSeibiBuhinSelected: null
  };
}

/*
 * buildDeletedStatusForConfirmEmitApplyEffectState を WorkAndBuhin.vue から分離し、
 * 削除確定 emit 前に component 側へ代入する buhinSelected の対象と値を固定するため。
 */
export function buildDeletedStatusForConfirmEmitApplyEffectState(
  deletedStatus: DeletedStatusForConfirmEmitState
): DeletedStatusForConfirmEmitApplyEffectState {
  return {
    ...deletedStatus
  };
}

/*
 * buildDeletedStatusForConfirmEmitTargetApplyEffectState を WorkAndBuhin.vue から分離し、
 * 削除確定 emit 前に buhinSelected を反映する対象配列、index、値を固定するため。
 */
export function buildDeletedStatusForConfirmEmitTargetApplyEffectState(params: {
  effectState: DeletedStatusForConfirmEmitApplyEffectState;
  activeWorkIndex: number;
}): DeletedStatusForConfirmEmitTargetApplyEffectState {
  return {
    shouldApply: params.effectState.shouldApply,
    shouldApplySeibiBuhinSelected:
      params.effectState.seibiBuhinSelected != null,
    seibiBuhinSelectedIndex: params.activeWorkIndex,
    seibiBuhinSelected: params.effectState.seibiBuhinSelected,
    shouldApplyManualSeibiBuhinSelected:
      params.effectState.manualSeibiBuhinSelected != null,
    manualSeibiBuhinSelectedIndex: params.activeWorkIndex,
    manualSeibiBuhinSelected: params.effectState.manualSeibiBuhinSelected,
    shouldApplyDataBuhinSelected:
      params.effectState.dataBuhinSelected != null,
    dataBuhinSelected: params.effectState.dataBuhinSelected
  };
}

/*
 * buildSeibiCleanupState を WorkAndBuhin.vue から分離し、
 * 保存確定 payload と status/emit 判定 を親側置換や単体テストで固定するため。
 */
export function buildSeibiCleanupState(data: any): SeibiCleanupState {
  const seibiList = data && data.seibiList && data.seibiList.length > 0
    ? cleanupSeibiListManualBuhin(data.seibiList)
    : null;

  if (!data || !data.manualSeibiList || data.manualSeibiList.length === 0) {
    return {
      seibiList,
      manualSeibiList: null
    };
  }

  const selectedManualSeibiList = data.manualSeibiList.filter(
    (seibi: any) => seibi.buhinSelected > 0
  );
  const manualSeibiSource = selectedManualSeibiList.length > 0
    ? selectedManualSeibiList
    : data.manualSeibiList;

  return {
    seibiList,
    manualSeibiList: cleanupSeibiListManualBuhin(deepCopy(manualSeibiSource))
  };
}

/*
 * buildSeibiCleanupApplyEffectState を WorkAndBuhin.vue から分離し、
 * cleanup 後の seibiList/manualSeibiList を component 側で反映する条件と値を固定するため。
 */
export function buildSeibiCleanupApplyEffectState(
  cleanupState: SeibiCleanupState
): SeibiCleanupApplyEffectState {
  return {
    shouldApplySeibiList: !!cleanupState.seibiList,
    seibiList: cleanupState.seibiList,
    shouldApplyManualSeibiList: !!cleanupState.manualSeibiList,
    manualSeibiList: cleanupState.manualSeibiList
  };
}

/*
 * buildWorkBuhinSaveState を WorkAndBuhin.vue から分離し、
 * 保存確定 payload と status/emit 判定 を親側置換や単体テストで固定するため。
 */
export function buildWorkBuhinSaveState(
  work: any,
  selectedBuhinList: BuhinDetail[],
  manualBuhinList: BuhinDetail[],
  options: WorkBuhinSaveOptions
): WorkBuhinSaveState {
  const workBuhinList = toArray<BuhinDetail>(work && work.buhinList);
  const osusumeBuhinList = toArray<BuhinDetail>(work && work.osusumeBuhinList);
  const selectedList = toArray<BuhinDetail>(selectedBuhinList);
  let buhinList = buildBuhinList(work, workBuhinList, selectedList, options);

  if (buhinList.length > 0) {
    buhinList = normalizeSaveBuhinList(buhinList, options.activeBuhinShubetsuId);
  }

  const manualSaveList = buildManualBuhinList(manualBuhinList);
  if (options.isRecommendManualWithOsusume) {
    return buildRecommendManualSaveState(buhinList, osusumeBuhinList, manualSaveList);
  }

  return {
    buhinList: applyDiscount(work, buhinList),
    manualBuhinList: manualSaveList
  };
}

/*
 * buildWorkBuhinSaveApplyEffectState を WorkAndBuhin.vue から分離し、
 * 保存リストを work に反映する対象と値を component 側で固定して適用するため。
 */
export function buildWorkBuhinSaveApplyEffectState(
  saveState: WorkBuhinSaveState
): WorkBuhinSaveApplyEffectState {
  return {
    buhinList: saveState.buhinList,
    shouldApplyOsusumeBuhinList: !!saveState.osusumeBuhinList,
    osusumeBuhinList: saveState.osusumeBuhinList || null,
    manualBuhinList: saveState.manualBuhinList
  };
}

/*
 * addBuhinIsDelete を WorkAndBuhin.vue から分離し、
 * 保存確定 payload と status/emit 判定 を親側置換や単体テストで固定するため。
 */
export function addBuhinIsDelete(list: BuhinDetail[]): BuhinDetail[] {
  return list.map((buhin: BuhinDetail) => ({ ...buhin, isDelete: 0 }));
}

function buildBuhinList(
  work: any,
  workBuhinList: BuhinDetail[],
  selectedList: BuhinDetail[],
  options: WorkBuhinSaveOptions
): BuhinDetail[] {
  let buhinList: BuhinDetail[] = [];
  const hasLegacyOsusumeList =
    work && work.osusumeBhinList && work.osusumeBuhinList && work.osusumeBuhinList.length > 0;

  if (
    ((workBuhinList.length > 0) || hasLegacyOsusumeList) &&
    selectedList.length > 0
  ) {
    const buhinDupList: string[] = [];
    const updatedWorkBuhinList = workBuhinList.map((buhin: BuhinDetail) => {
      const foundBuhin = selectedList.find(
        (item: BuhinDetail) => buhin.buhinOsusumeId != null
          ? item.buhinOsusumeId == buhin.buhinOsusumeId
          : item.buhinOsusumeId == null && !!buhin.buhinCd && item.buhinCd == buhin.buhinCd
      );
      if (foundBuhin != null) {
        if (buhin.buhinCd) {
          buhinDupList.push(String(buhin.buhinCd));
        }
        return {
          ...buhin,
          isDelete: 0,
          quantity: foundBuhin.quantity,
          buhinPriceZn: foundBuhin.buhinPriceZn
        };
      }
      return { ...buhin, isDelete: 1 };
    });

    const buhinFilterList = addBuhinIsDelete(
      selectedList.filter(
        (buhin: BuhinDetail) =>
          buhin.buhinCd &&
          !isJunseiBuhinCode(buhin.buhinCd) &&
          buhinDupList.indexOf(String(buhin.buhinCd)) == -1
      )
    );
    buhinList = [...buhinFilterList, ...updatedWorkBuhinList];

    if (work && work.osusumeBuhinList && work.osusumeBuhinList.length > 0) {
      work.osusumeBuhinList.forEach((buhin: BuhinDetail) => {
        const foundBuhin = selectedList.find(
          (item: BuhinDetail) => item.buhinOsusumeId == buhin.buhinOsusumeId
        );
        const alreadyIncluded = buhinList.some(
          (item: BuhinDetail) => item.buhinOsusumeId == buhin.buhinOsusumeId
        );
        if (foundBuhin != null && !alreadyIncluded) {
          buhinList.push({ ...foundBuhin, isDelete: 0 });
        }
      });
    }
  } else if (selectedList.length > 0) {
    const buhinFilterList = selectedList.filter((buhin: BuhinDetail) => {
      return (
        (buhin.buhinCd && !isJunseiBuhinCode(buhin.buhinCd)) ||
        (Object.prototype.hasOwnProperty.call(buhin, "buhinOsusumeId") && buhin.buhinOsusumeId)
      );
    });
    buhinList = addBuhinIsDelete(buhinFilterList);
  } else if (workBuhinList.length > 0) {
    buhinList = workBuhinList
      .filter((buhin: BuhinDetail) => buhin.id)
      .map((buhin: BuhinDetail) => ({ ...buhin, isDelete: 1 }));
  }

  return buhinList;
}

function normalizeSaveBuhinList(
  buhinList: BuhinDetail[],
  activeBuhinShubetsuId: number
): BuhinDetail[] {
  return buhinList
    .filter((buhin: BuhinDetail) => buhin.isDelete != 1 || buhin.id)
    .map((e: BuhinDetail) => {
      const buhin = deepCopy(e);
      if (Object.prototype.hasOwnProperty.call(buhin, "defaultQuantity")) {
        delete buhin.defaultQuantity;
      }
      const buhinShubetsuId = buhin.isExtreme == 1 ? 0 : activeBuhinShubetsuId;
      return { ...buhin, buhinShubetsuId };
    });
}

function buildRecommendManualSaveState(
  buhinList: BuhinDetail[],
  osusumeBuhinList: BuhinDetail[],
  manualBuhinList: BuhinDetail[]
): WorkBuhinSaveState {
  const osusume = buhinList.filter((item: BuhinDetail) => item.buhinOsusumeId != null);
  const arrOsusumeId = osusume.map((item: BuhinDetail) => item.buhinOsusumeId);
  const normalBuhin = buhinList.filter(
    (item: BuhinDetail) =>
      !Object.prototype.hasOwnProperty.call(item, "buhinOsusumeId") ||
      item.buhinOsusumeId == null
  );
  let nextOsusumeBuhinList = osusumeBuhinList;

  if (osusumeBuhinList.length > 0) {
    const osusumeDelete = osusumeBuhinList
      .filter(
        (item: BuhinDetail) =>
          arrOsusumeId.indexOf(item.buhinOsusumeId) == -1 && item.id != null
      )
      .map((item: BuhinDetail) => ({ ...item, isDelete: 1 }));
    nextOsusumeBuhinList = [...osusume, ...osusumeDelete];
  }

  return {
    buhinList: normalBuhin,
    osusumeBuhinList: nextOsusumeBuhinList,
    manualBuhinList
  };
}

function applyDiscount(work: any, buhinList: BuhinDetail[]): BuhinDetail[] {
  const isDiscount = work && work.isDiscount === 1;
  return buhinList.map((buhin: BuhinDetail) => {
    if (isDiscount && buhin.notFreeTanka == 0) {
      return {
        ...buhin,
        buhinPriceZk: 0,
        buhinPriceZn: 0,
        kingakuZk: 0,
        kingakuZn: 0
      };
    }
    return { ...buhin };
  });
}

function buildManualBuhinList(manualBuhinList: BuhinDetail[]): BuhinDetail[] {
  return toArray<BuhinDetail>(manualBuhinList).map((buhin: BuhinDetail) => {
    const isDelete = buhin.quantity > 0 ? 0 : 1;
    return { ...buhin, isDelete };
  });
}

function cleanupSeibiListManualBuhin(seibiList: any[]): any[] {
  return toArray<any>(seibiList).map((work: any) => ({
    ...work,
    manualBuhinList: toArray<BuhinDetail>(work && work.manualBuhinList).filter(
      (buhin: BuhinDetail) => buhin.isDelete != 1 && buhin.quantity > 0
    )
  }));
}

function deepCopy<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}
